import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import DateRangePicker from '../../components/common/DateRangePicker';
import { usePagedData } from '../../hooks/usePagedData';
import { amazonShipmentsApi } from '../../api/endpoints';
import { InboundShipmentList, InboundShipmentSummary } from '../../types';
import Select from '../../components/common/Select';
import {
  FiRefreshCw, FiSearch, FiPackage, FiTruck, FiCheckCircle, FiArchive,
} from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { toast } from 'react-toastify';

/* ------------------------------------------------------------------ */
/*  Status badge                                                       */
/* ------------------------------------------------------------------ */

const inboundStatusColors: Record<string, { color: string; bg: string }> = {
  WORKING: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)' },
  READY_TO_SHIP: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
  SHIPPED: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
  IN_TRANSIT: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.12)' },
  DELIVERED: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  CHECKED_IN: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  RECEIVING: { color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.12)' },
  CLOSED: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  CANCELLED: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
  DELETED: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
  ERROR: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
};

const InboundStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = inboundStatusColors[status] || { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
      fontWeight: 500, color: cfg.color, backgroundColor: cfg.bg, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cfg.color, display: 'inline-block', flexShrink: 0 }} />
      {status}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Stat card                                                          */
/* ------------------------------------------------------------------ */

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconBg, iconColor, label, value }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', borderRadius: '10px',
    backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border)',
  }}>
    <div style={{
      width: '36px', height: '36px', borderRadius: '8px', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      backgroundColor: iconBg, color: iconColor, fontSize: '16px', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)' }}>
        {value}
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const InboundShipmentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState<InboundShipmentSummary | null>(null);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<InboundShipmentList>(amazonShipmentsApi.inbound.list, {
    initialSortBy: 'createdAt',
    initialSortDesc: true,
  });

  useEffect(() => {
    let cancelled = false;
    amazonShipmentsApi.inbound.summary()
      .then((resp) => { if (!cancelled) setSummary(resp.data); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [data]);

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleDestFcFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        destinationFulfillmentCenterId: value === '' ? undefined : value,
      }));
    },
    [setFilters]
  );

  const handleDateFromChange = useCallback((value: string) => {
    setDateFrom(value);
    setFilters((prev: Record<string, any>) => ({ ...prev, dateFrom: value || undefined }));
  }, [setFilters]);

  const handleDateToChange = useCallback((value: string) => {
    setDateTo(value);
    setFilters((prev: Record<string, any>) => ({ ...prev, dateTo: value || undefined }));
  }, [setFilters]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await amazonShipmentsApi.inbound.sync();
      toast.success(t('amazonShipments.syncStarted'));
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('amazonShipments.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [refresh, t]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const destFcOptions = useMemo(() => {
    const fcs = Object.keys(summary?.byDestinationFc || {});
    return [
      { value: '', label: t('amazonShipments.allDestinations') },
      ...fcs.map(fc => ({ value: fc, label: fc })),
    ];
  }, [summary, t]);

  const columns = useMemo(() => [
    {
      key: 'shipmentId',
      header: t('amazonShipments.shipmentId'),
      sortable: true,
      render: (row: InboundShipmentList) => (
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', fontWeight: 600 }}>
          {row.shipmentId}
        </span>
      ),
    },
    {
      key: 'shipmentName',
      header: t('amazonShipments.shipmentName'),
      className: 'col-hide-mobile',
      render: (row: InboundShipmentList) => (
        <span style={{
          fontSize: '13px',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: '260px', display: 'inline-block',
        }}>
          {row.shipmentName || '—'}
        </span>
      ),
    },
    {
      key: 'destFc',
      header: t('amazonShipments.destFc'),
      sortable: true,
      render: (row: InboundShipmentList) => (
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: '8px',
          fontSize: '11px', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
          backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text)',
        }}>
          {row.destinationFulfillmentCenterId}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      render: (row: InboundShipmentList) => <InboundStatusBadge status={row.shipmentStatus} />,
    },
    {
      key: 'units',
      header: t('amazonShipments.units'),
      render: (row: InboundShipmentList) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px' }}>
          <span style={{ color: 'var(--text)', fontWeight: 600 }}>
            {row.totalUnitsShipped} {t('amazonShipments.shipped')}
          </span>
          {row.totalUnitsReceived > 0 && (
            <span style={{ color: '#10b981' }}>
              {row.totalUnitsReceived} {t('amazonShipments.received')}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'itemCount',
      header: t('amazonShipments.itemCount'),
      className: 'col-hide-mobile',
      render: (row: InboundShipmentList) => (
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          minWidth: '26px', height: '22px', padding: '0 8px',
          borderRadius: '11px', fontSize: '12px', fontWeight: 600,
          backgroundColor: 'rgba(107, 114, 128, 0.1)', color: 'var(--text)',
        }}>
          {row.itemCount}
        </span>
      ),
    },
    {
      key: 'confirmedNeedByDate',
      header: t('amazonShipments.needBy'),
      className: 'col-hide-mobile',
      render: (row: InboundShipmentList) => (
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {formatDate(row.confirmedNeedByDate)}
        </span>
      ),
    },
  ], [t]);

  return (
    <div className="page-container page-container--wide">
      <div className="page-header">
        <h1>{t('amazonShipments.inboundTitle')}</h1>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? t('amazonShipments.syncing') : t('amazonShipments.syncWithAmazon')}
          </button>
        </div>
      </div>

      {summary && (
        <div className="po-stats-grid">
          <StatCard
            icon={<FiPackage />}
            iconBg="rgba(59, 130, 246, 0.15)"
            iconColor="#3b82f6"
            label={t('amazonShipments.statTotalShipments')}
            value={summary.totalShipments}
          />
          <StatCard
            icon={<FiTruck />}
            iconBg="rgba(245, 158, 11, 0.15)"
            iconColor="#f59e0b"
            label={t('amazonShipments.statShipped')}
            value={summary.shippedCount}
          />
          <StatCard
            icon={<FiArchive />}
            iconBg="rgba(124, 58, 237, 0.15)"
            iconColor="#7c3aed"
            label={t('amazonShipments.statReceiving')}
            value={summary.receivingCount}
          />
          <StatCard
            icon={<FiCheckCircle />}
            iconBg="rgba(16, 185, 129, 0.15)"
            iconColor="#10b981"
            label={t('amazonShipments.statClosed')}
            value={summary.closedCount}
          />
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder={t('amazonShipments.searchInboundPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: t('common.allStatuses') },
              { value: 'WORKING', label: 'WORKING' },
              { value: 'READY_TO_SHIP', label: 'READY_TO_SHIP' },
              { value: 'SHIPPED', label: 'SHIPPED' },
              { value: 'IN_TRANSIT', label: 'IN_TRANSIT' },
              { value: 'DELIVERED', label: 'DELIVERED' },
              { value: 'CHECKED_IN', label: 'CHECKED_IN' },
              { value: 'RECEIVING', label: 'RECEIVING' },
              { value: 'CLOSED', label: 'CLOSED' },
              { value: 'CANCELLED', label: 'CANCELLED' },
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder={t('common.allStatuses')}
          />

          <Select
            options={destFcOptions}
            value={String(filters.destinationFulfillmentCenterId ?? '')}
            onChange={handleDestFcFilter}
            placeholder={t('amazonShipments.allDestinations')}
          />
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={handleDateFromChange}
          onDateToChange={handleDateToChange}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={6} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
        />
      )}
    </div>
  );
};

export default InboundShipmentsPage;
